import React, { useState, useEffect } from 'react';
import {
  StyleSheet,
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  TextInput,
  Modal,
  Alert,
  ActivityIndicator,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { supabase } from '../../api/supabase';
import { hashPassword } from '../../utils/passwordHash';
import { useAuthStore } from '../../stores/authStore';
import theme from '../../theme';
import {
  ROLES,
  PERMISSIONS,
  PERMISSION_GROUPS,
  PERMISSION_LABELS,
  PERMISSION_DESCRIPTIONS,
  ROLE_PERMISSIONS,
  ROLE_LABELS,
  ROLE_DESCRIPTIONS,
  type Role,
  type Permission,
} from '../../constants/permissions';

interface AdminUser {
  id: string;
  user_id?: string;
  name: string;
  email: string;
  role: Role;
  permissions_added: Permission[];
  permissions_removed: Permission[];
  is_active: boolean;
  password_hash?: string;
  last_login?: string;
  created_at: string;
}

export const UserManagementScreenNew: React.FC = () => {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState<AdminUser | null>(null);
  
  // Get current user and checkSession function
  const currentUser = useAuthStore((state) => state.user);
  const checkSession = useAuthStore((state) => state.checkSession);
  
  // Form state
  const [formName, setFormName] = useState('');
  const [formEmail, setFormEmail] = useState('');
  const [formPassword, setFormPassword] = useState('');
  const [formRole, setFormRole] = useState<Role>('operator');
  const [formPermissionsAdded, setFormPermissionsAdded] = useState<Permission[]>([]);
  const [formPermissionsRemoved, setFormPermissionsRemoved] = useState<Permission[]>([]);
  const [formIsActive, setFormIsActive] = useState(true);
  
  // Permission Matrix state
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [showPermissionMatrix, setShowPermissionMatrix] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('admin_users')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setUsers(data || []);
    } catch (error: any) {
      console.error('Error fetching users:', error);
      Alert.alert('Error', 'Failed to load users: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const openAddUserModal = () => {
    setEditingUser(null);
    setFormName('');
    setFormEmail('');
    setFormPassword('');
    setFormRole('operator');
    setFormPermissionsAdded([]);
    setFormPermissionsRemoved([]);
    setFormIsActive(true);
    setShowPermissionMatrix(false);
    setExpandedGroups(new Set());
    setModalVisible(true);
  };

  const openEditUserModal = (user: AdminUser) => {
    setEditingUser(user);
    setFormName(user.name);
    setFormEmail(user.email);
    setFormPassword('');
    setFormRole(user.role);
    setFormPermissionsAdded(user.permissions_added || []);
    setFormPermissionsRemoved(user.permissions_removed || []);
    setFormIsActive(user.is_active);
    setShowPermissionMatrix(false);
    setExpandedGroups(new Set());
    setModalVisible(true);
  };
  
  // Get role default permissions
  const getRolePermissions = (role: Role): Permission[] => {
    return ROLE_PERMISSIONS[role] || [];
  };

  // Calculate effective permissions for display
  const getEffectivePermissions = (): Permission[] => {
    const rolePerms = getRolePermissions(formRole);
    const added = formPermissionsAdded || [];
    const removed = formPermissionsRemoved || [];
    
    // Start with role permissions, add custom additions, remove custom removals
    const effective = new Set([...rolePerms, ...added]);
    removed.forEach(perm => effective.delete(perm));
    
    return Array.from(effective);
  };

  // Check permission status: 'default' | 'added' | 'removed' | 'none'
  const getPermissionStatus = (permission: Permission): 'default' | 'added' | 'removed' | 'none' => {
    const rolePerms = getRolePermissions(formRole);
    const isInRole = rolePerms.includes(permission);
    const isAdded = formPermissionsAdded.includes(permission);
    const isRemoved = formPermissionsRemoved.includes(permission);

    if (isRemoved) return 'removed';
    if (isAdded) return 'added';
    if (isInRole) return 'default';
    return 'none';
  };

  // Toggle permission
  const togglePermission = (permission: Permission) => {
    const status = getPermissionStatus(permission);
    const rolePerms = getRolePermissions(formRole);
    const isInRole = rolePerms.includes(permission);

    if (status === 'default') {
      // Remove from role default
      setFormPermissionsRemoved([...formPermissionsRemoved, permission]);
    } else if (status === 'added') {
      // Remove from added list
      setFormPermissionsAdded(formPermissionsAdded.filter(p => p !== permission));
    } else if (status === 'removed') {
      // Restore to default (remove from removed list)
      setFormPermissionsRemoved(formPermissionsRemoved.filter(p => p !== permission));
    } else {
      // Not in role, add it
      setFormPermissionsAdded([...formPermissionsAdded, permission]);
    }
  };

  // Reset to role defaults
  const resetToRoleDefaults = () => {
    Alert.alert(
      'Reset to Role Defaults',
      'This will clear all custom permissions and restore role defaults. Continue?',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Reset',
          style: 'destructive',
          onPress: () => {
            setFormPermissionsAdded([]);
            setFormPermissionsRemoved([]);
          },
        },
      ]
    );
  };

  // Toggle permission group expansion
  const toggleGroup = (groupName: string) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(groupName)) {
      newExpanded.delete(groupName);
    } else {
      newExpanded.add(groupName);
    }
    setExpandedGroups(newExpanded);
  };

  const handleSaveUser = async () => {
    if (!formName.trim() || !formEmail.trim()) {
      Alert.alert('Error', 'Name and email are required');
      return;
    }

    if (!editingUser && !formPassword) {
      Alert.alert('Error', 'Password is required for new users');
      return;
    }

    try {
      setLoading(true);

      if (editingUser) {
        // Update existing user
        const updateData: any = {
          name: formName.trim(),
          email: formEmail.trim().toLowerCase(),
          role: formRole,
          permissions_added: formPermissionsAdded,
          permissions_removed: formPermissionsRemoved,
          is_active: formIsActive,
          updated_at: new Date().toISOString(),
        };

        // If password is provided, hash it
        if (formPassword) {
          const hashedPassword = await hashPassword(formPassword);
          updateData.password_hash = hashedPassword;
        }

        const { error } = await supabase
          .from('admin_users')
          .update(updateData)
          .eq('id', editingUser.id);

        if (error) throw error;
        Alert.alert('Success', 'User updated successfully');
        
        // CRITICAL: If we just updated the currently logged-in user's permissions,
        // force a session refresh to update their permissions immediately
        if (currentUser && editingUser.id === currentUser.id) {
          console.log('🔄 Updated current user permissions, refreshing session...');
          await checkSession();
          Alert.alert(
            'Permissions Updated',
            'Your permissions have been updated. Some screens may now be visible or hidden.',
            [{ text: 'OK' }]
          );
        }
      } else {
        // Create new user
        const hashedPassword = await hashPassword(formPassword);

        const { error } = await supabase
          .from('admin_users')
          .insert({
            name: formName.trim(),
            email: formEmail.trim().toLowerCase(),
            role: formRole,
            password_hash: hashedPassword,
            permissions_added: formPermissionsAdded,
            permissions_removed: formPermissionsRemoved,
            is_active: formIsActive,
          });

        if (error) throw error;
        Alert.alert('Success', 'User created successfully');
      }

      setModalVisible(false);
      fetchUsers();
    } catch (error: any) {
      console.error('Error saving user:', error);
      Alert.alert('Error', 'Failed to save user: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = (user: AdminUser) => {
    // Check user permission for DELETE_USERS
    const hasDeletePermission = useAuthStore.getState().hasPermission(PERMISSIONS.DELETE_USERS);
    
    if (!hasDeletePermission) {
      Alert.alert(
        'Permission Required',
        'You do not have permission to delete users',
        [{ text: 'OK' }]
      );
      return;
    }
    
    Alert.alert(
      'Delete User',
      `Are you sure you want to delete ${user.name}?`,
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Delete',
          style: 'destructive',
          onPress: async () => {
            try {
              const { error } = await supabase
                .from('admin_users')
                .delete()
                .eq('id', user.id);

              if (error) throw error;
              Alert.alert('Success', 'User deleted successfully');
              fetchUsers();
            } catch (error: any) {
              console.error('Error deleting user:', error);
              Alert.alert('Error', 'Failed to delete user: ' + error.message);
            }
          },
        },
      ]
    );
  };

  const togglePermissionAdded = (permission: Permission) => {
    setFormPermissionsAdded(prev =>
      prev.includes(permission)
        ? prev.filter(p => p !== permission)
        : [...prev, permission]
    );
  };

  const togglePermissionRemoved = (permission: Permission) => {
    setFormPermissionsRemoved(prev =>
      prev.includes(permission)
        ? prev.filter(p => p !== permission)
        : [...prev, permission]
    );
  };

  const getRoleColor = (role: Role) => {
    switch (role) {
      case 'super_admin': return theme.colors.error;
      case 'financial_manager': return theme.colors.success;
      case 'operations_manager': return theme.colors.warning;
      case 'accountant': return theme.colors.info;
      case 'operator': return theme.colors.primary;
      default: return theme.colors.gray600;
    }
  };

  const getRoleStats = () => {
    return {
      total: users.length,
      superAdmin: users.filter(u => u.role === 'super_admin').length,
      financial: users.filter(u => u.role === 'financial_manager').length,
      operations: users.filter(u => u.role === 'operations_manager').length,
      accountant: users.filter(u => u.role === 'accountant').length,
      operator: users.filter(u => u.role === 'operator').length,
    };
  };

  const stats = getRoleStats();

  if (loading && users.length === 0) {
    return (
      <View style={styles.centerContainer}>
        <ActivityIndicator size="large" color={theme.colors.primary} />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <Text style={styles.headerTitle}>User Management</Text>
        <TouchableOpacity style={styles.addButton} onPress={openAddUserModal}>
          <Ionicons name="add" size={24} color="#fff" />
          <Text style={styles.addButtonText}>Add User</Text>
        </TouchableOpacity>
      </View>

      {/* Statistics */}
      <View style={styles.statsContainer}>
        <View style={styles.statCard}>
          <Text style={styles.statValue}>{stats.total}</Text>
          <Text style={styles.statLabel}>Total Users</Text>
        </View>
        <View style={styles.statCard}>
          <Text style={styles.statValue}>{stats.superAdmin}</Text>
          <Text style={styles.statLabel}>Super Admin</Text>
        </View>
        <View style={styles.statCard}>
          <Text style={styles.statValue}>{stats.financial}</Text>
          <Text style={styles.statLabel}>Financial</Text>
        </View>
        <View style={styles.statCard}>
          <Text style={styles.statValue}>{stats.operations}</Text>
          <Text style={styles.statLabel}>Operations</Text>
        </View>
        <View style={styles.statCard}>
          <Text style={styles.statValue}>{stats.accountant}</Text>
          <Text style={styles.statLabel}>Accountant</Text>
        </View>
      </View>

      {/* User List */}
      <ScrollView style={styles.content}>
        {users.map(user => (
          <View key={user.id} style={styles.userCard}>
            <View style={styles.userHeader}>
              <View style={[styles.roleBadge, { backgroundColor: getRoleColor(user.role) + '20' }]}>
                <Text style={[styles.roleText, { color: getRoleColor(user.role) }]}>
                  {ROLE_LABELS[user.role]}
                </Text>
              </View>
              <View style={styles.userActions}>
                <TouchableOpacity
                  style={styles.iconButton}
                  onPress={() => openEditUserModal(user)}
                >
                  <Ionicons name="create-outline" size={20} color={theme.colors.primary} />
                </TouchableOpacity>
                <TouchableOpacity
                  style={styles.iconButton}
                  onPress={() => handleDeleteUser(user)}
                >
                  <Ionicons name="trash-outline" size={20} color={theme.colors.error} />
                </TouchableOpacity>
              </View>
            </View>
            <Text style={styles.userName}>{user.name}</Text>
            <Text style={styles.userEmail}>{user.email}</Text>
            <Text style={styles.userDate}>
              Created: {new Date(user.created_at).toLocaleDateString()}
            </Text>
            {!user.is_active && (
              <Text style={styles.inactiveText}>⚠️ Inactive</Text>
            )}
          </View>
        ))}
      </ScrollView>

      {/* Add/Edit User Modal */}
      <Modal
        visible={modalVisible}
        animationType="slide"
        transparent={true}
        onRequestClose={() => setModalVisible(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>
                {editingUser ? 'Edit User' : 'Add New User'}
              </Text>
              <TouchableOpacity onPress={() => setModalVisible(false)}>
                <Ionicons name="close" size={24} color={theme.colors.textPrimary} />
              </TouchableOpacity>
            </View>

            <ScrollView style={styles.modalBody}>
              {/* Basic Info */}
              <Text style={styles.sectionTitle}>Basic Information</Text>
              
              <Text style={styles.inputLabel}>Name</Text>
              <TextInput
                style={styles.input}
                value={formName}
                onChangeText={setFormName}
                placeholder="Enter name"
                placeholderTextColor={theme.colors.gray400}
              />

              <Text style={styles.inputLabel}>Email</Text>
              <TextInput
                style={styles.input}
                value={formEmail}
                onChangeText={setFormEmail}
                placeholder="Enter email"
                keyboardType="email-address"
                autoCapitalize="none"
                placeholderTextColor={theme.colors.gray400}
              />

              <Text style={styles.inputLabel}>
                Password {editingUser && '(leave blank to keep current)'}
              </Text>
              <TextInput
                style={styles.input}
                value={formPassword}
                onChangeText={setFormPassword}
                placeholder={editingUser ? 'Enter new password' : 'Enter password'}
                secureTextEntry
                placeholderTextColor={theme.colors.gray400}
              />

              {/* Role Selection */}
              <Text style={styles.sectionTitle}>Role</Text>
              {Object.entries(ROLES).map(([key, roleValue]) => (
                <TouchableOpacity
                  key={roleValue}
                  style={[
                    styles.roleOption,
                    formRole === roleValue && styles.roleOptionSelected,
                  ]}
                  onPress={() => setFormRole(roleValue as Role)}
                >
                  <View style={styles.roleOptionContent}>
                    <Text style={[
                      styles.roleOptionLabel,
                      formRole === roleValue && styles.roleOptionLabelSelected,
                    ]}>
                      {ROLE_LABELS[roleValue as Role]}
                    </Text>
                    <Text style={styles.roleOptionDescription}>
                      {ROLE_DESCRIPTIONS[roleValue as Role]}
                    </Text>
                  </View>
                  {formRole === roleValue && (
                    <Ionicons name="checkmark-circle" size={24} color={theme.colors.primary} />
                  )}
                </TouchableOpacity>
              ))}

              {/* Permission Matrix */}
              <View style={styles.permissionMatrixContainer}>
                <TouchableOpacity
                  style={styles.permissionMatrixHeader}
                  onPress={() => setShowPermissionMatrix(!showPermissionMatrix)}
                >
                  <View style={styles.permissionMatrixHeaderLeft}>
                    <Ionicons
                      name={showPermissionMatrix ? 'chevron-down' : 'chevron-forward'}
                      size={20}
                      color={theme.colors.gray700}
                    />
                    <Text style={styles.permissionMatrixTitle}>
                      Custom Permissions (Optional)
                    </Text>
                  </View>
                  <View style={styles.permissionBadge}>
                    <Text style={styles.permissionBadgeText}>
                      {formPermissionsAdded.length > 0 && `+${formPermissionsAdded.length} `}
                      {formPermissionsRemoved.length > 0 && `-${formPermissionsRemoved.length}`}
                      {formPermissionsAdded.length === 0 && formPermissionsRemoved.length === 0 && 'None'}
                    </Text>
                  </View>
                </TouchableOpacity>

                {showPermissionMatrix && (
                  <View style={styles.permissionMatrixContent}>
                    <Text style={styles.permissionMatrixDescription}>
                      Customize permissions beyond role defaults. Gray = role default, Green = custom added, Red = removed.
                    </Text>

                    {/* Reset Button */}
                    {(formPermissionsAdded.length > 0 || formPermissionsRemoved.length > 0) && (
                      <TouchableOpacity
                        style={styles.resetButton}
                        onPress={resetToRoleDefaults}
                      >
                        <Ionicons name="refresh" size={16} color={theme.colors.error} />
                        <Text style={styles.resetButtonText}>Reset to Role Defaults</Text>
                      </TouchableOpacity>
                    )}

                    {/* Permission Groups */}
                    {PERMISSION_GROUPS.map((group) => (
                      <View key={group.name} style={styles.permissionGroup}>
                        <TouchableOpacity
                          style={styles.permissionGroupHeader}
                          onPress={() => toggleGroup(group.name)}
                        >
                          <View style={styles.permissionGroupHeaderLeft}>
                            <Text style={styles.permissionGroupIcon}>{group.icon}</Text>
                            <View>
                              <Text style={styles.permissionGroupName}>{group.name}</Text>
                              <Text style={styles.permissionGroupDescription}>
                                {group.description}
                              </Text>
                            </View>
                          </View>
                          <Ionicons
                            name={expandedGroups.has(group.name) ? 'chevron-up' : 'chevron-down'}
                            size={20}
                            color={theme.colors.gray500}
                          />
                        </TouchableOpacity>

                        {expandedGroups.has(group.name) && (
                          <View style={styles.permissionList}>
                            {group.permissions.map((permission) => {
                              const status = getPermissionStatus(permission);
                              const isChecked = status === 'default' || status === 'added';
                              
                              return (
                                <TouchableOpacity
                                  key={permission}
                                  style={styles.permissionItem}
                                  onPress={() => togglePermission(permission)}
                                >
                                  <View style={styles.permissionItemLeft}>
                                    <Ionicons
                                      name={isChecked ? 'checkbox' : 'square-outline'}
                                      size={22}
                                      color={
                                        status === 'added'
                                          ? theme.colors.success
                                          : status === 'removed'
                                          ? theme.colors.error
                                          : status === 'default'
                                          ? theme.colors.gray500
                                          : theme.colors.gray300
                                      }
                                    />
                                    <View style={styles.permissionItemText}>
                                      <Text
                                        style={[
                                          styles.permissionLabel,
                                          status === 'removed' && styles.permissionLabelRemoved,
                                        ]}
                                      >
                                        {PERMISSION_LABELS[permission]}
                                      </Text>
                                      {status !== 'none' && (
                                        <Text style={styles.permissionStatus}>
                                          {status === 'default' && '(Role default)'}
                                          {status === 'added' && '(Custom added)'}
                                          {status === 'removed' && '(Removed)'}
                                        </Text>
                                      )}
                                    </View>
                                  </View>
                                  {status === 'removed' && (
                                    <Ionicons name="close-circle" size={18} color={theme.colors.error} />
                                  )}
                                </TouchableOpacity>
                              );
                            })}
                          </View>
                        )}
                      </View>
                    ))}

                    {/* Summary */}
                    <View style={styles.permissionSummary}>
                      <Text style={styles.permissionSummaryTitle}>Total Effective Permissions</Text>
                      <Text style={styles.permissionSummaryCount}>
                        {getEffectivePermissions().length} permissions
                      </Text>
                    </View>
                  </View>
                )}
              </View>

              {/* Active Status */}
              <TouchableOpacity
                style={styles.checkboxRow}
                onPress={() => setFormIsActive(!formIsActive)}
              >
                <Ionicons
                  name={formIsActive ? 'checkbox' : 'square-outline'}
                  size={24}
                  color={formIsActive ? theme.colors.primary : theme.colors.gray400}
                />
                <Text style={styles.checkboxLabel}>Active User</Text>
              </TouchableOpacity>
            </ScrollView>

            <View style={styles.modalFooter}>
              <TouchableOpacity
                style={[styles.button, styles.buttonSecondary]}
                onPress={() => setModalVisible(false)}
              >
                <Text style={styles.buttonSecondaryText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.button, styles.buttonPrimary]}
                onPress={handleSaveUser}
              >
                <Text style={styles.buttonPrimaryText}>
                  {editingUser ? 'Update' : 'Create'}
                </Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.gray50,
  },
  centerContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: theme.colors.white,
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border,
  },
  headerTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: theme.colors.textPrimary,
  },
  addButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: theme.colors.primary,
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 8,
  },
  addButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
    marginLeft: 8,
  },
  statsContainer: {
    flexDirection: 'row',
    padding: 16,
    gap: 12,
    flexWrap: 'wrap',
  },
  statCard: {
    flex: 1,
    minWidth: 100,
    backgroundColor: theme.colors.white,
    padding: 16,
    borderRadius: 8,
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  statValue: {
    fontSize: 28,
    fontWeight: 'bold',
    color: theme.colors.primary,
  },
  statLabel: {
    fontSize: 12,
    color: theme.colors.gray600,
    marginTop: 4,
    textAlign: 'center',
  },
  content: {
    flex: 1,
    padding: 16,
  },
  userCard: {
    backgroundColor: theme.colors.white,
    padding: 16,
    borderRadius: 12,
    marginBottom: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  userHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  roleBadge: {
    paddingHorizontal: 12,
    paddingVertical: 4,
    borderRadius: 12,
  },
  roleText: {
    fontSize: 12,
    fontWeight: '600',
  },
  userActions: {
    flexDirection: 'row',
    gap: 8,
  },
  iconButton: {
    padding: 8,
  },
  userName: {
    fontSize: 18,
    fontWeight: 'bold',
    color: theme.colors.textPrimary,
    marginBottom: 4,
  },
  userEmail: {
    fontSize: 14,
    color: theme.colors.gray600,
    marginBottom: 4,
  },
  userDate: {
    fontSize: 12,
    color: theme.colors.gray500,
  },
  inactiveText: {
    fontSize: 12,
    color: theme.colors.error,
    marginTop: 4,
    fontWeight: '600',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    backgroundColor: theme.colors.white,
    borderRadius: 16,
    width: Platform.OS === 'web' ? 600 : '90%',
    maxHeight: '90%',
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border,
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: theme.colors.textPrimary,
  },
  modalBody: {
    padding: 20,
    maxHeight: 500,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: theme.colors.textPrimary,
    marginTop: 16,
    marginBottom: 12,
  },
  inputLabel: {
    fontSize: 14,
    fontWeight: '500',
    color: theme.colors.textPrimary,
    marginBottom: 8,
  },
  input: {
    borderWidth: 1,
    borderColor: theme.colors.border,
    borderRadius: 8,
    padding: 12,
    fontSize: 16,
    marginBottom: 16,
    color: theme.colors.textPrimary,
    backgroundColor: theme.colors.white,
  },
  roleOption: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 12,
    borderWidth: 1,
    borderColor: theme.colors.border,
    borderRadius: 8,
    marginBottom: 8,
  },
  roleOptionSelected: {
    borderColor: theme.colors.primary,
    backgroundColor: theme.colors.primaryLight + '10',
  },
  roleOptionContent: {
    flex: 1,
  },
  roleOptionLabel: {
    fontSize: 16,
    fontWeight: '600',
    color: theme.colors.textPrimary,
  },
  roleOptionLabelSelected: {
    color: theme.colors.primary,
  },
  roleOptionDescription: {
    fontSize: 12,
    color: theme.colors.gray600,
    marginTop: 2,
  },
  checkboxRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 16,
  },
  checkboxLabel: {
    fontSize: 16,
    color: theme.colors.textPrimary,
    marginLeft: 8,
  },
  modalFooter: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    padding: 20,
    borderTopWidth: 1,
    borderTopColor: theme.colors.border,
    gap: 12,
  },
  button: {
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 8,
    minWidth: 100,
    alignItems: 'center',
  },
  buttonSecondary: {
    backgroundColor: theme.colors.gray200,
  },
  buttonSecondaryText: {
    color: theme.colors.textPrimary,
    fontSize: 16,
    fontWeight: '600',
  },
  buttonPrimary: {
    backgroundColor: theme.colors.primary,
  },
  buttonPrimaryText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
  },
  // Permission Matrix Styles
  permissionMatrixContainer: {
    marginTop: 20,
    borderWidth: 1,
    borderColor: theme.colors.border,
    borderRadius: 8,
    overflow: 'hidden',
  },
  permissionMatrixHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 12,
    backgroundColor: theme.colors.gray100,
  },
  permissionMatrixHeaderLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  permissionMatrixTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: theme.colors.textPrimary,
  },
  permissionBadge: {
    backgroundColor: theme.colors.primary,
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 12,
  },
  permissionBadgeText: {
    color: '#fff',
    fontSize: 12,
    fontWeight: '600',
  },
  permissionMatrixContent: {
    padding: 16,
    backgroundColor: theme.colors.white,
  },
  permissionMatrixDescription: {
    fontSize: 13,
    color: theme.colors.gray600,
    marginBottom: 16,
    lineHeight: 18,
  },
  resetButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingVertical: 8,
    paddingHorizontal: 12,
    backgroundColor: '#fee',
    borderRadius: 6,
    alignSelf: 'flex-start',
    marginBottom: 16,
  },
  resetButtonText: {
    color: theme.colors.error,
    fontSize: 14,
    fontWeight: '600',
  },
  permissionGroup: {
    marginBottom: 12,
    borderWidth: 1,
    borderColor: theme.colors.border,
    borderRadius: 8,
    overflow: 'hidden',
  },
  permissionGroupHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 12,
    backgroundColor: theme.colors.gray50,
  },
  permissionGroupHeaderLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    flex: 1,
  },
  permissionGroupIcon: {
    fontSize: 24,
  },
  permissionGroupName: {
    fontSize: 15,
    fontWeight: '600',
    color: theme.colors.textPrimary,
  },
  permissionGroupDescription: {
    fontSize: 12,
    color: theme.colors.gray600,
    marginTop: 2,
  },
  permissionList: {
    backgroundColor: theme.colors.white,
  },
  permissionItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 12,
    borderTopWidth: 1,
    borderTopColor: theme.colors.border,
  },
  permissionItemLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    flex: 1,
  },
  permissionItemText: {
    flex: 1,
  },
  permissionLabel: {
    fontSize: 14,
    color: theme.colors.textPrimary,
    fontWeight: '500',
  },
  permissionLabelRemoved: {
    textDecorationLine: 'line-through',
    color: theme.colors.gray500,
  },
  permissionStatus: {
    fontSize: 12,
    color: theme.colors.gray500,
    marginTop: 2,
    fontStyle: 'italic',
  },
  permissionSummary: {
    marginTop: 16,
    padding: 12,
    backgroundColor: '#e3f2fd',
    borderRadius: 8,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  permissionSummaryTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: theme.colors.primary,
  },
  permissionSummaryCount: {
    fontSize: 16,
    fontWeight: 'bold',
    color: theme.colors.primary,
  },
});
